import { spacing } from 'ext-ui/style/spacing'
import { css } from 'styled-components'

/**
 * Only the stack between sections lives here — the section cards themselves are
 * `ext-ui`'s `Box`, whose `default` variant is the bordered, shadowless
 * container the design calls for. Same gap as the Statements tab so the two
 * card-management tabs sit on one rhythm.
 *
 * Stacks the stats roll-up, the lifted legacy Overview sections, and
 * EX-40633's Recent Transactions. EX-40630's Card Details drops in beside them.
 */
export const sections = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(9)};
`

/**
 * Below this the two panels stop fitting side by side — the chart's category
 * axis labels start colliding well before the card details rows do. Same value
 * as `CELLS_BREAKPOINT` in `cardOverviewStats.style.ts`, so the stats band and
 * the row beneath it reflow together rather than at two different widths.
 */
const DETAILS_ROW_BREAKPOINT = '1100px'

/** Marks the Card details cell inside `detailsRow` so it can opt out of stretching. */
export const CARD_DETAILS_CELL_CLASS = 'AccountOverviewTab-cardDetailsCell'

/**
 * Card details beside Spend by category — the 1/3 : 2/3 split from the design.
 *
 * `minmax(0, …)` on both tracks rather than a bare `1fr`/`2fr`: a grid track's
 * default `min-width: auto` refuses to shrink below its content, and the chart
 * measures its own container to size itself, so it would push the row wider than
 * its parent instead of scaling down.
 *
 * Only rendered when BOTH panels are present — the page falls back to a
 * full-width card details panel otherwise, so there is no single-child case to
 * style here. See the `showSections` note in `AccountOverviewTab`.
 *
 * The container stays `align-items: stretch` — `CardOverviewSpendByCategory`'s
 * `panel` is `height: 100%` and depends on that default to fill the row (see
 * `cardOverviewSpendByCategory.style.ts`). Card details opts out on its own
 * cell instead, via `CARD_DETAILS_CELL_CLASS`, so it hugs its content height
 * without collapsing the chart beside it.
 */
export const detailsRow = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  gap: ${spacing(9)};
  align-items: stretch;

  > .${CARD_DETAILS_CELL_CLASS} {
    align-self: start;
  }

  @media (max-width: ${DETAILS_ROW_BREAKPOINT}) {
    grid-template-columns: minmax(0, 1fr);
  }
`
